//
// DISCLAIMER
//
// Copyright 2019-2026 ArangoDB GmbH, Cologne, Germany
//

import { RouteComponentProps } from "react-router-dom";
import { Button, Dropdown, Header, Divider } from "semantic-ui-react";
import { Duration, Timestamp } from "../../api/googleTypes";
import {
  Model,
  DeploymentSize as ApiDeploymentSize,
  Deployment_ModelSpec as ApiDeployment_ModelSpec,
  Deployment_ServersSpec as ApiDeployment_ServersSpec,
  Deployment_DiskAutoSizeSettings as APIDiskAutoSizeSettings,
  MemoryReserve,
  UpdateDeploymentMemoryReservesRequest as ApiUpdateDeploymentMemoryReservesRequest,
  UpdateDeploymentTopologyAwarenessRequest as ApiUpdateDeploymentTopologyAwarenessRequest,
  VolumeInfo as ApiVolumeInfo,
  DeploymentResources as ApiDeploymentResources,
  UpdateKubernetesResourcesPatchesRequest as ApiUpdateKubernetesResourcesPatchesRequest,
  JSONPatch,
  MemoryFactors as ApiMemoryFactors,
  CPUFactors as ApiCPUFactors,
  NodeSizeDetails as ApiNodeSizeDetails,
  AddonResources,
} from "../../api/lib";

import { EditBox, Field, FieldContent as FC, FieldLabelWide as FL } from "../../ui/lib";
import { AsyncResult } from "../../util/Types";
import { IWithRefreshProps } from "../../util/WithRefresh";
import { CommentCreationArgs } from "../comments/CommentTypes";
import { TopologyAwarenessView } from "./TopologyAwarenessView";
import UpdateKubernetesResourcesPatchModal from "./UpdateKubernetesResourcesPatchModal";
import { VSTSupportView } from "./ConfigurationView/VSTSupportView";
import { ArangoDBPlatformEnableView } from "./ConfigurationView/ArangoDBPlatformEnableView";
import EnvoyAccessLogView from "./ConfigurationView/EnvoyAccessLogView";
import IConfigurationViewItemProps from "./ConfigurationView/IConfigurationViewItemProps";
import { ConfigureArchitectureTypeView } from "./ConfigurationView/ConfigureArchitectureTypeView";
import { ConfigurationResourceServersView } from "./ConfigurationResourceServersView";
import { MemoryReserveView } from "./MemoryReserveView";

export interface IConfigurationViewArgs extends IWithRefreshProps, RouteComponentProps, CommentCreationArgs, IConfigurationViewItemProps {
  organizationId: string;
  regionId: string;
  model?: ApiDeployment_ModelSpec;
  servers?: ApiDeployment_ServersSpec;
  size?: ApiDeploymentSize;
  nodeSizeDetails: ApiNodeSizeDetails;
  cpuCoordinatorPercent: number;
  cpuDbServerPercent: number;
  onUpdateCPURatio: (newCPURatio: string) => Promise<AsyncResult<void>>;
  can_edit_cpu_ratio: boolean;
  cpuResources?: ApiDeploymentResources;
  memoryCoordinatorPercent: number;
  memoryDbServerPercent: number;
  onUpdateMemoryRatio: (newMemoryRatio: string) => Promise<AsyncResult<void>>;
  can_edit_memory_ratio: boolean;
  memoryResources?: ApiDeploymentResources;
  agencyResourceFactor: number;
  gatewayResourceFactor: number;
  can_edit_agency_resource_factor: boolean;
  can_edit_gateway_resource_factor: boolean;
  onUpdateAgencyResourceFactor: (newFactor: string) => Promise<AsyncResult<void>>;
  onUpdateGatewayResourceFactor: (newFactor: string) => Promise<AsyncResult<void>>;
  communicationMethod: string;
  can_edit_communication_method: boolean;
  onUpdateCommunicationMethod: (newMethod: string) => void;
  agentsMemoryReserve?: MemoryReserve;
  onUpdateMemoryReserve: (request: ApiUpdateDeploymentMemoryReservesRequest) => any;
  diskAutoSizeSettings?: APIDiskAutoSizeSettings;
  coordinatorsMemoryReserve?: MemoryReserve;
  dbserversMemoryReserve?: MemoryReserve;
  canEditMemoryReserves: boolean;
  isTopologyAware: boolean;
  numberOfZonesOverride: number;
  canUpdateTopologyAwareness: boolean;
  onUpdateTopologyAwareness: (request: ApiUpdateDeploymentTopologyAwarenessRequest) => any;
  schedulingPoliciesEnabled: boolean;

  agentsVolumeInfo?: ApiVolumeInfo;
  dbserverVolumeInfo?: ApiVolumeInfo;
  canEditVolumeInfos: boolean;
  onSaveVolumeInfos: (newAgentVolumeInfo?: ApiVolumeInfo, newDbserverVolumeInfo?: ApiVolumeInfo) => Promise<{ errorMessage: string }>;
  onEditAgentsVolumeInfos: () => void;
  onCancelEditAgentsVolumeInfos: () => void;
  editAgentsVolumeInfos: boolean;
  onEditDBServersVolumeInfos: () => void;
  onCancelEditDBServersVolumeInfos: () => void;
  editDBServersVolumeInfos: boolean;
  processingUpdateVolumeInfos: boolean;
  customStorageClassName?: string;
  diskPerformanceId?: string;
  diskPerformanceLocked?: boolean;
  lastDiskPerformanceUpdatedAt?: Timestamp;
  lastDiskSizeUpdatedAt?: Timestamp;
  diskRateLimitPeriod?: Duration;
  diskRateLimitActive?: Duration;
  canEditDiskPerformanceLocked: boolean;
  onUpdateDiskPerformanceLocked: (locked: boolean, useRegularVolumeIinfo: boolean) => void;
  canEditDiskPerformance: boolean;
  editDiskPerformance: boolean;
  onEditDiskPerformance: () => void;
  onCancelEditDiskPerformance: () => void;
  onDiskPerformanceChange: (id: string) => void;
  useShardRebalancer: boolean;
  canUpdateUseShardRebalacner: boolean;
  onUpdateDeploymentUseShardRebalancer: (newValue: boolean) => void;
  canUpdateKubernetesResourcesPatches: boolean;
  arangoDeploymentPatches?: JSONPatch[];
  cloudDeploymentPatches?: JSONPatch[];
  onUpdateKubernetesResourcesPatches: (data: ApiUpdateKubernetesResourcesPatchesRequest) => Promise<AsyncResult<void>>;
  onSetSchedulingPolciiesEnabled: (enabled: boolean) => void;

  memoryFactors?: ApiMemoryFactors;
  cpuFactors?: ApiCPUFactors;
  onUpdateCPUFactors: (newFactor: string) => Promise<AsyncResult<void>>;
  onUpdateMemoryFactors: (newFactor: string) => Promise<AsyncResult<void>>;
  canUpdateMemoryFactors?: boolean;
  canUpdateCPUFactors?: boolean;
  canSetSchedulingPoliciesEnabled: boolean;
  schedulingVersion: number;
  canUpdateSchedulingVersion: boolean;
  onUpgradeSchedulingVersion: () => void;
  canInstallSchedulingDefaults: boolean;
  onInstallSchedulingDefaults: () => void;
  onUpdateDiskSize: (newValue: number, ignoreMax: boolean) => Promise<AsyncResult<void>>;
  canEditDiskSize: boolean;
  canEditDiskSizeIgnoreMax: boolean;
  /** When true, deployment uses shared LB (no dedicated LB carve / row). */
  useSharedLoadbalancer?: boolean;
  /** Replace full factor objects (v3 Resources tab). Preserves all roles. */
  onUpdateMemoryFactorsValues?: (factors: ApiMemoryFactors) => Promise<AsyncResult<void>>;
  onUpdateCPUFactorsValues?: (factors: ApiCPUFactors) => Promise<AsyncResult<void>>;
  /** Absolute gateway/LB carve requests (deployment stamp, else node-size defaults). */
  addonResources?: AddonResources;
  canUpdateAddonResources?: boolean;
  onUpdateAddonResources?: (resources: AddonResources) => Promise<AsyncResult<void>>;
  refreshDeploymentInfo?: () => void;
}

export const ConfigurationView = ({ ...args }: IConfigurationViewArgs) => {
  const model = args.model || {};
  const hasAgency = Model.hasAgency(model.model || "");
  const isCluster = Model.isCluster(model.model || "");
  const showLegacyResourceAllocation = args.schedulingVersion < 3;

  return (
    <div>
      <Header sub>Configuration</Header>
      <Field>
        <FL>Model</FL>
        <FC>{model.model || "?"}</FC>
      </Field>
      <Field>
        <FL>Size</FL>
        <FC>
          {!!model.node_size_id && (
            <span>
              {args.nodeSizeDetails.name} ({args.nodeSizeDetails.cpu_size})
            </span>
          )}
        </FC>
      </Field>
      <Field>
        <FL>Communication method</FL>
        <FC>
          <span>{args.communicationMethod}</span>
          {args.can_edit_communication_method && (
            <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
              <Dropdown.Menu>
                <Dropdown.Item key="headless" text="Use headless service" onClick={() => args.onUpdateCommunicationMethod("headless")} />
                <Dropdown.Item key="headless-dns" text="Use headless DNS" onClick={() => args.onUpdateCommunicationMethod("headless-dns")} />
                <Dropdown.Item key="ip" text="Use IP" onClick={() => args.onUpdateCommunicationMethod("ip")} />
              </Dropdown.Menu>
            </Dropdown>
          )}
        </FC>
      </Field>

      <VSTSupportView requireComment={args.requireComment} requirePermission={args.requirePermission} />

      <ArangoDBPlatformEnableView
        requireComment={args.requireComment}
        requirePermission={args.requirePermission}
        organizationId={args.organizationId}
        regionId={args.regionId}
      />

      <ConfigureArchitectureTypeView requireComment={args.requireComment} requirePermission={args.requirePermission} />

      <EnvoyAccessLogView requireComment={args.requireComment} requirePermission={args.requirePermission} />

      <Field>
        <FL>Custom StorageClass name</FL>
        <FC>{args.customStorageClassName || "-"}</FC>
      </Field>
      {args.canUpdateKubernetesResourcesPatches && (
        <Field>
          <FL>Update ArangoDeployment kubernetes Patches</FL>
          <FC>
            <UpdateKubernetesResourcesPatchModal
              resourceType="ArangoDeployment"
              patches={args.arangoDeploymentPatches || []}
              onUpdateKubernetesResourcesPatches={args.onUpdateKubernetesResourcesPatches}
            />
          </FC>
        </Field>
      )}
      {args.canUpdateKubernetesResourcesPatches && (
        <Field>
          <FL>Update CloudDeployment kubernetes Patches</FL>
          <FC>
            <UpdateKubernetesResourcesPatchModal
              resourceType="CloudDeployment"
              patches={args.cloudDeploymentPatches || []}
              onUpdateKubernetesResourcesPatches={args.onUpdateKubernetesResourcesPatches}
            />
          </FC>
        </Field>
      )}
      <Field>
        <FL>Scheduling version</FL>
        <FC>
          <span>{args.schedulingVersion}</span>
        </FC>
      </Field>
      {args.schedulingVersion < 3 ? (
        <Field>
          <FL>Scheduling policies propgation from NodeSize enabled</FL>
          <FC>
            <span>{args.schedulingPoliciesEnabled ? "Yes" : "No"}</span>
            {args.canSetSchedulingPoliciesEnabled && (
              <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
                <Dropdown.Menu>
                  {!!args.schedulingPoliciesEnabled && (
                    <Dropdown.Item key="remove-hm" text="Disable scheduling policies" onClick={() => args.onSetSchedulingPolciiesEnabled(false)} />
                  )}
                  {!args.schedulingPoliciesEnabled && (
                    <Dropdown.Item key="set-hm" text="Enable scheduling policies" onClick={() => args.onSetSchedulingPolciiesEnabled(true)} />
                  )}
                </Dropdown.Menu>
              </Dropdown>
            )}
            {args.canUpdateSchedulingVersion && (
              <Button basic size="mini" style={{ marginLeft: "0.5em" }} content="Upgrade to scheduling v3" onClick={args.onUpgradeSchedulingVersion} />
            )}
          </FC>
        </Field>
      ) : (
        args.canInstallSchedulingDefaults && (
          <Field>
            <FL>Scheduling defaults</FL>
            <FC>
              <Button basic size="mini" content="Reapply scheduling defaults" onClick={args.onInstallSchedulingDefaults} />
            </FC>
          </Field>
        )
      )}
      {isCluster && (
        <div>
          <Field>
            <FL>Topology aware</FL>
            <FC>
              <TopologyAwarenessView
                isTopologyAware={args.isTopologyAware}
                numberOfZonesOverride={args.numberOfZonesOverride}
                canUpdateTopologyAwareness={args.canUpdateTopologyAwareness}
                onUpdateTopologyAwareness={args.onUpdateTopologyAwareness}
                createComment={args.createComment}
              />
            </FC>
          </Field>
          <Field>
            <FL>Use Shard Re-balancer</FL>
            <FC>
              <span>{args.useShardRebalancer ? "Yes" : "No"}</span>
              {args.canUpdateUseShardRebalacner && (
                <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
                  <Dropdown.Menu>
                    {args.useShardRebalancer && (
                      <Dropdown.Item
                        key="remove-hm"
                        text="Turn off automatic shard re-balancing"
                        onClick={() => args.onUpdateDeploymentUseShardRebalancer(false)}
                      />
                    )}
                    {!args.useShardRebalancer && (
                      <Dropdown.Item key="set-hm" text="Turn on automatic shard re-balancing" onClick={() => args.onUpdateDeploymentUseShardRebalancer(true)} />
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </FC>
          </Field>
        </div>
      )}

      {showLegacyResourceAllocation && (
        <>
          <Divider hidden />
          <Header size="tiny">Resource Allocation</Header>
          {isCluster && (
            <Field>
              <FL>Memory ratio (Coordinator/DB server)</FL>
              <FC>
                <EditBox
                  {...args}
                  initialValue={`${args.memoryCoordinatorPercent}/${args.memoryDbServerPercent}`}
                  update={args.onUpdateMemoryRatio}
                  placeholder={"Memory ratio e.g. 25/75"}
                  editable={args.can_edit_memory_ratio}
                  label="Memory ratio (Coordinator/DB server)"
                />
              </FC>
            </Field>
          )}
          <Field>
            <FL>Memory factors (Coordinator/DBServer)</FL>
            <FC>
              <EditBox
                {...args}
                initialValue={`${(args.memoryFactors || {}).coordinator_factor || "-"}/${(args.memoryFactors || {}).dbserver_factor || "-"}`}
                update={args.onUpdateMemoryFactors}
                placeholder={"Memory Factor eg: 0.5/0.7 (use 0/0 for default)"}
                editable={args.canUpdateMemoryFactors}
                label="Memory factors (Coordinator/DBServer)"
              />
            </FC>
          </Field>
          {isCluster && (
            <Field>
              <FL>CPU ratio (Coordinator/DB server)</FL>
              <FC>
                <EditBox
                  {...args}
                  initialValue={`${args.cpuCoordinatorPercent}/${args.cpuDbServerPercent}`}
                  update={args.onUpdateCPURatio}
                  placeholder={"CPU ratio e.g. 25/75"}
                  editable={args.can_edit_cpu_ratio}
                  label="CPU ratio (Coordinator/DB server)"
                />
              </FC>
            </Field>
          )}
          <Field>
            <FL>CPU factors (Coordinator/DBServer)</FL>
            <FC>
              <EditBox
                {...args}
                initialValue={`${(args.cpuFactors || {}).coordinator_factor || "-"}/${(args.cpuFactors || {}).dbserver_factor || "-"}`}
                update={args.onUpdateCPUFactors}
                placeholder={"CPU Factor eg: 0.5/0.7 (use 0/0 for default)"}
                editable={args.canUpdateCPUFactors}
                label="CPU factors (Coordinator/DBServer)"
              />
            </FC>
          </Field>
          {hasAgency && (
            <Field>
              <FL>Agency resources factor</FL>
              <FC>
                <EditBox
                  {...args}
                  initialValue={`${args.agencyResourceFactor}`}
                  update={args.onUpdateAgencyResourceFactor}
                  placeholder={"Agency resources factor (e.g. 2.0)"}
                  editable={args.can_edit_agency_resource_factor}
                  label="Agency resources factor"
                />
              </FC>
            </Field>
          )}
          <ConfigurationResourceServersView {...args} />
        </>
      )}

      {args.schedulingVersion >= 3 && (
        <>
          <Divider hidden />
          <Header size="tiny">Memory reserves</Header>
          <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
            ArangoDB memory-reserve settings (env / database parameters), not scheduling carve. Absolute and relative reserve per role.
          </p>
          {isCluster && (
            <Field>
              <FL>Coordinators</FL>
              <FC>
                <MemoryReserveView
                  memoryReserve={args.coordinatorsMemoryReserve}
                  canEditMemoryReserves={args.canEditMemoryReserves}
                  onUpdateMemoryReserve={(reserve: MemoryReserve) => {
                    args.onUpdateMemoryReserve({ coordinators_memory_reserve: reserve });
                  }}
                  createComment={args.createComment}
                />
              </FC>
            </Field>
          )}
          <Field>
            <FL>{isCluster ? "Database servers" : "Single server"}</FL>
            <FC>
              <MemoryReserveView
                memoryReserve={args.dbserversMemoryReserve}
                canEditMemoryReserves={args.canEditMemoryReserves}
                onUpdateMemoryReserve={(reserve: MemoryReserve) => {
                  args.onUpdateMemoryReserve({ dbservers_memory_reserve: reserve });
                }}
                createComment={args.createComment}
              />
            </FC>
          </Field>
          {hasAgency && (
            <Field>
              <FL>Agents</FL>
              <FC>
                <MemoryReserveView
                  memoryReserve={args.agentsMemoryReserve}
                  canEditMemoryReserves={args.canEditMemoryReserves}
                  onUpdateMemoryReserve={(reserve: MemoryReserve) => {
                    args.onUpdateMemoryReserve({ agents_memory_reserve: reserve });
                  }}
                  createComment={args.createComment}
                />
              </FC>
            </Field>
          )}
        </>
      )}
    </div>
  );
};
